// src/components/auth/VibeLogin.jsx - Login component for returning users
import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  User, Eye, EyeOff, Lock, ArrowLeft, 
  AlertTriangle, CheckCircle, LogIn,
} from 'lucide-react';
import { usePrivacyAuth } from '@/context/PrivacyAuthContext';
import { useApp } from '@/context/AppContext';

// Card component
const Card = ({ children, className, onClick }) => (
  <div 
    className={`bg-slate-800/50 backdrop-blur-lg rounded-xl border border-slate-700/50 ${className}`} 
    onClick={onClick}
  >
    {children}
  </div>
);

// Button component
const Button = ({ children, onClick, disabled, variant, size, className, type = 'button' }) => {
  const baseClasses = 'px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center text-sm sm:text-base';
  const variantClasses = {
    secondary: 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600',
    primary: 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white',
    ghost: 'bg-transparent hover:bg-slate-700 text-slate-300 hover:text-white',
  };
  const sizeClasses = {
    lg: 'px-4 py-3 sm:px-6 sm:py-3 text-base sm:text-lg',
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant] || variantClasses.primary} ${sizeClasses[size] || ''} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

const VibeLogin = ({ onComplete = () => {}, onBackToSignup = () => {} }) => {
  const [formData, setFormData] = useState({
    username: '',
    passphrase: '',
  });
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { login, auth } = usePrivacyAuth();
  const { setCurrentView } = useApp();

  // Handle input changes
  const handleInputChange = useCallback((field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  }, [error]);

  // Handle form submission
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim()) {
      setError('Username is required');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await login(formData.username, formData.passphrase);
      
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onComplete();
        }, 1500);
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, login, onComplete]);

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            Welcome back!
          </h2>
          <p className="text-slate-300 text-lg">
            You're all logged in. Let's continue your journey.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => setCurrentView('home')}
            className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
          
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome Back
          </h1>
          <p className="text-slate-300">
            Sign in to continue your inner voice journey
          </p>
        </div>

        {/* Login Form */}
        <Card className="p-6">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            {/* Username Field */}
            <div>
              <label className="block text-white font-medium mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Username
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInputChange('username', e.target.value);
                }}
                placeholder="Enter your username"
                className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                autoComplete="username"
                disabled={loading}
              />
            </div>

            {/* Passphrase Field */}
            <div>
              <label className="block text-white font-medium mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Passphrase
              </label>
              <div className="relative">
                <input
                  type={showPassphrase ? 'text' : 'password'}
                  value={formData.passphrase}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleInputChange('passphrase', e.target.value);
                  }}
                  placeholder="Enter your passphrase (optional)"
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 pr-12"
                  autoComplete="current-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassphrase(!showPassphrase)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                  disabled={loading}
                >
                  {showPassphrase ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-slate-400 text-xs mt-1">
                Leave empty if you didn't set a passphrase
              </p>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              disabled={loading || !formData.username.trim()}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing In...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </div>
              )}
            </Button>

            {/* Quick Login Info */}
            <div className="text-center pt-4 border-t border-slate-600">
              <p className="text-slate-400 text-sm mb-2">
                Don't have an account?
              </p>
              <Button
                variant="ghost"
                onClick={() => setCurrentView('signup')}
                className="text-purple-300 hover:text-white"
              >
                Create Account Instead
              </Button>
            </div>
          </form>
        </Card>

        {/* Privacy Note */}
        <div className="text-center mt-6">
          <p className="text-slate-400 text-xs">
            Your data is stored locally and privately. No personal information is shared.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VibeLogin;